import { Close, Send } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Collapse,
  Divider,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { NewOrderTypes } from '../../../common/enums/enums';
import { getSelectorActionsBasedOnType } from './helper';
import CompletionTime from './steps/CompletionTime';
import Extras from './steps/Extras';
import Services from './steps/Services';
import Vehicle from './steps/Vehicle';
import Vendor from './steps/Vendor';

export default function SinglePage({
  onToggleMode,
  onSave,
  orderType = NewOrderTypes.SIMPLE_ORDER,
}) {
  const { t } = useTranslation(['orders', 'global']);
  const dispatch = useDispatch();
  const { clearOrderAction, isLoadingSelector } =
    getSelectorActionsBasedOnType(orderType);
  const [showAlertToggle, setShowAlertToggle] = useState(true);
  const [vendorStepValid, setVendorStepValid] = useState(true);
  const [servicesStepValid, setServicesStepValid] = useState(true);
  const [vehicleStepValid, setVehicleStepValid] = useState(true);
  const [completionStepValid, setCompletionStepValid] = useState(true);
  const isLoading = useSelector(isLoadingSelector);
  const history = useHistory();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedVendorDepartment, setSelectedVendorDepartment] =
    useState(true);

  const handleChangeValidVendor = isValid => {
    setVendorStepValid(isValid);
  };
  const handleChangeValidServices = isValid => {
    setServicesStepValid(isValid);
  };
  const handleChangeValidVehicle = isValid => {
    setVehicleStepValid(isValid);
  };
  const handleChangeValidCompletionTime = isValid => {
    setCompletionStepValid(isValid);
  };

  const buttonDisabled =
    !vendorStepValid ||
    !servicesStepValid ||
    !vehicleStepValid ||
    !completionStepValid;

  const boxProps = {
    pt: { xs: 2, md: 4 },
    pb: 1,
  };

  const goToWorkOrder = () => {
    dispatch(clearOrderAction());
    history.goBack();
  };

  return (
    <>
      <Stack alignItems="center" mb={8}>
        <Collapse in={showAlertToggle} sx={{ width: '100%' }}>
          <Alert
            severity="info"
            action={
              <>
                <Button size="small" onClick={onToggleMode}>
                  {(smallScreen && t('newOrder.swithModeSmall')) ||
                    t('newOrder.switchToWizard')}
                </Button>
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => setShowAlertToggle(false)}
                >
                  <Close fontSize="small" />
                </IconButton>
              </>
            }
          >
            {t('newOrder.changeToWizard')}
          </Alert>
        </Collapse>
        <Stack
          maxWidth="835px"
          width="100%"
          divider={<Divider />}
          p={{ xs: 3, md: 0 }}
        >
          <Vendor
            onValidStep={handleChangeValidVendor}
            boxProps={boxProps}
            orderType={orderType}
          />
          <Services
            onValidStep={handleChangeValidServices}
            boxProps={boxProps}
            overflow={true}
            orderType={orderType}
            setSelectedVendorDepartment={setSelectedVendorDepartment}
          />
          <Vehicle
            onValidStep={handleChangeValidVehicle}
            boxProps={boxProps}
            divider={null}
            orderType={orderType}
          />
          <Extras
            boxProps={boxProps}
            showAlert={false}
            autoFocus={false}
            orderType={orderType}
            selectedVendorDepartment={selectedVendorDepartment}
          />
          <CompletionTime
            boxProps={boxProps}
            onValidStep={handleChangeValidCompletionTime}
            orderType={orderType}
          />
        </Stack>
      </Stack>
      <Stack
        p={2}
        position="fixed"
        width={{ xs: '100%', md: 'calc(100% - 240px)' }}
        bottom={0}
        direction="row"
        justifyContent="space-around"
        sx={{
          backgroundColor: theme.palette.background.paper,
          zIndex: 1,
        }}
      >
        <Button
          variant="outlined"
          disabled={isLoading}
          onClick={goToWorkOrder}
          sx={{
            color: theme.palette.text.primary,
            borderColor: theme.palette.divider,
          }}
        >
          {t('global:genericButtons.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          color="success"
          disabled={buttonDisabled}
          onClick={onSave}
          loading={isLoading}
          endIcon={<Send />}
        >
          {t('newOrder.createOrder')}
        </LoadingButton>
      </Stack>
    </>
  );
}
